import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { paymentsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Clock, Loader2, XCircle } from 'lucide-react';

const POLL_MS = 4000;
const GIVE_UP_MS = 2 * 60 * 1000;

/**
 * Where Startbutton sends the user back after paying. Asks the server
 * (which checks with Startbutton) until the payment is settled.
 */
export default function PaymentReturnPage() {
  const [params] = useSearchParams();
  const reference = params.get('reference') || '';
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { t } = useTranslation();
  const [payment, setPayment] = useState(null);
  const [state, setState] = useState(reference ? 'checking' : 'error');
  const alive = useRef(true);

  useEffect(() => () => { alive.current = false; }, []);

  useEffect(() => {
    if (!reference) return undefined;
    const started = Date.now();
    let timer;
    const check = async () => {
      try {
        const { data } = await paymentsAPI.get(reference);
        if (!alive.current) return;
        setPayment(data);
        if (data.status === 'succeeded') {
          setState('paid');
          updateUser?.({ ...user, subscription_status: 'active', subscription_tier: data.plan_tier });
          return;
        }
        if (data.status === 'failed') {
          setState('failed');
          return;
        }
      } catch {
        if (!alive.current) return;
      }
      if (Date.now() - started > GIVE_UP_MS) {
        setState('waiting');
        return;
      }
      timer = setTimeout(check, POLL_MS);
    };
    check();
    return () => clearTimeout(timer);
    // updateUser/user only matter once the payment is settled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  const naira = payment?.amount_ngn ? `₦${Number(payment.amount_ngn).toLocaleString()}` : '';
  return (
    <div className="p-6 max-w-md mx-auto text-center animate-fadeIn" data-testid="payment-return">
      {state === 'checking' && (
        <>
          <Loader2 className="w-12 h-12 animate-spin text-farm-primary mx-auto mb-4" />
          <h1 className="font-heading text-xl font-bold mb-2">{t('payments.return.checking')}</h1>
        </>
      )}
      {state === 'paid' && (
        <>
          <CheckCircle2 className="w-14 h-14 text-farm-success mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold mb-2">{t('payments.return.paid')}</h1>
          <p className="text-muted-foreground text-sm mb-6">
            {t('payments.return.paidBody', {
              amount: naira,
              plan: payment.description,
              date: new Date(payment.period_end).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }),
            })}
          </p>
        </>
      )}
      {state === 'failed' && (
        <>
          <XCircle className="w-14 h-14 text-destructive mx-auto mb-4" />
          <h1 className="font-heading text-xl font-bold mb-2">{t('payments.return.failed')}</h1>
          <p className="text-muted-foreground text-sm mb-6">{t('payments.return.failedBody')}</p>
        </>
      )}
      {state === 'waiting' && (
        <>
          <Clock className="w-14 h-14 text-farm-primary mx-auto mb-4" />
          <h1 className="font-heading text-xl font-bold mb-2">{t('payments.return.waiting')}</h1>
          <p className="text-muted-foreground text-sm mb-6">{t('payments.return.waitingBody')}</p>
        </>
      )}
      {state === 'error' && (
        <h1 className="font-heading text-xl font-bold mb-6">{t('payments.return.unknown')}</h1>
      )}
      {state !== 'checking' && (
        <div className="flex flex-col gap-2">
          <Button className="bg-farm-primary" onClick={() => navigate('/dashboard', { replace: true })}>
            {t('payments.return.home')}
          </Button>
          <Button variant="outline" onClick={() => navigate('/payments')}>{t('payments.title')}</Button>
        </div>
      )}
    </div>
  );
}
